import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Download } from 'lucide-react';
import { SYMBOL_DATA, INTERVALS, PERIODS } from '../constants/symbols';
import { ETF_SYMBOL_DATA } from '../constants/etfs';
import { chunkSymbols, runBatchesWithConcurrency } from '../lib/scannerUtils';
import { isRequestAborted } from '../lib/httpUtils';
import AutomaticScanner from './AutomaticScanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SCAN_BATCH_SIZE = 4;
const MAX_PARALLEL_SCAN_BATCHES = 2;
const MOBILE_SCANNER_OPEN_KEY = 'mobileScannerOpen';
const isPeriodOverSevenDays = (period) => {
  const match = /^(\d+)(d|wk|mo|y)$/.exec(period);
  if (!match) return true;

  const amount = Number(match[1]);
  const daysByUnit = { d: 1, wk: 7, mo: 30, y: 365 };
  return amount * daysByUnit[match[2]] > 7;
};

const toScanSignalDetails = (symbol, signals) => (signals || []).map(signal => ({
  symbol,
  signal_type: signal.signal_type || null,
  entry_price: signal.entry ?? null,
  stop_price: signal.stop ?? null,
  target_price: signal.target ?? null,
  breakout_confirmation_time: signal.breakout_confirmation_time,
  breakout_confirmation_candle: signal.breakout_confirmation_candle,
  retest_time: signal.retest_time,
  retest_candle: signal.retest_candle,
  confirmation_time: signal.confirmation_time,
  confirmation_candle: signal.confirmation_candle,
}));

const ScannerMenu = ({ onScanComplete, onScanNotification }) => {
  const { t } = useTranslation();
  const [isOptionsOpen, setIsOptionsOpen] = useState(() => {
    try {
      return localStorage.getItem(MOBILE_SCANNER_OPEN_KEY) !== 'false';
    } catch (err) {
      console.error('Failed to read mobile scanner preference:', err);
      return true;
    }
  });

  const toggleOptions = () => {
    const nextOpen = !isOptionsOpen;
    setIsOptionsOpen(nextOpen);
    try {
      localStorage.setItem(MOBILE_SCANNER_OPEN_KEY, String(nextOpen));
    } catch (err) {
      console.error('Failed to save mobile scanner preference:', err);
    }
  };
  const [formData, setFormData] = useState({
    symbols: [],
    period: '5d',
    interval: '5m',
    include_extended_hours: false
  });
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchSymbol, setSearchSymbol] = useState('');
  const [symbolUniverse, setSymbolUniverse] = useState('sp500');
  const symbolCatalog = symbolUniverse === 'etf' ? ETF_SYMBOL_DATA : SYMBOL_DATA;

  // Saved symbol lists state
  const [savedLists, setSavedLists] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showSavedLists, setShowSavedLists] = useState(false);

  // Fetch saved lists on component mount
  React.useEffect(() => {
    fetchSavedLists();
  }, []);

  const fetchSavedLists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/saved-symbols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedLists(response.data);
    } catch (err) {
      console.error('Failed to fetch saved lists:', err);
    }
  };

  const handleSaveList = async () => {
    if (!newListName.trim()) {
      alert(t('enter_list_name_alert'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/saved-symbols`,
        {
          name: newListName,
          symbols: formData.symbols
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(t('save_list_success', { name: newListName, count: formData.symbols.length }));
      setNewListName('');
      setShowSaveModal(false);
      fetchSavedLists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || t('save_list_failed'));
    }
  };

  const handleLoadList = (savedList) => {
    setFormData(prev => ({ ...prev, symbols: savedList.symbols }));
    setSuccess(t('load_list_success', { name: savedList.name, count: savedList.symbol_count }));
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDeleteList = async (listId, listName) => {
    if (!window.confirm(t('confirm_delete_list', { name: listName }))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/saved-symbols/${listId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('delete_list_success', { name: listName }));
      fetchSavedLists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || t('delete_list_failed'));
    }
  };

  const handleRunSavedList = async (savedList) => {
    setFormData(prev => ({ ...prev, symbols: savedList.symbols }));
    setSuccess(t('run_saved_list_initiated', { name: savedList.name }));
    
    // Trigger scan after a short delay
    setTimeout(() => {
      handleSubmit(new Event('submit'));
    }, 500);
  };

  // Enhanced filtering: search both symbol and company name
  const filteredSymbols = () => {
    const searchTerms = searchSymbol
      .split(',')
      .map(term => term.trim().toLowerCase())
      .filter(Boolean);

    if (searchTerms.length === 0) {
      return symbolCatalog;
    }

    return symbolCatalog.filter(item => {
      const symbol = item.symbol.toLowerCase();
      const name = item.name.toLowerCase();
      return searchTerms.some(term => symbol.includes(term) || name.includes(term));
    });
  };

  const handleSymbolToggle = (symbol) => {
    setFormData(prev => {
      const symbols = prev.symbols.includes(symbol)
        ? prev.symbols.filter(s => s !== symbol) // Remove if already selected
        : [...prev.symbols, symbol]; // Add if not selected
      
      return { ...prev, symbols };
    });
  };

  // Check if all filtered symbols are selected
  const areAllFilteredSelected = () => {
    const filtered = filteredSymbols();
    if (filtered.length === 0) return false;
    return filtered.every(item => formData.symbols.includes(item.symbol));
  };

  // Check if some but not all filtered symbols are selected
  const areSomeFilteredSelected = () => {
    const filtered = filteredSymbols();
    if (filtered.length === 0) return false;
    const selectedCount = filtered.filter(item => formData.symbols.includes(item.symbol)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  };

  // Handle master checkbox toggle
  const handleMasterCheckbox = () => {
    const filteredSymbolsList = filteredSymbols().map(item => item.symbol);
    const filteredSymbolSet = new Set(filteredSymbolsList);

    setFormData(prev => {
      const allFilteredSelected = filteredSymbolsList.every(symbol => prev.symbols.includes(symbol));
      return {
        ...prev,
        symbols: allFilteredSelected
          ? prev.symbols.filter(symbol => !filteredSymbolSet.has(symbol))
          : [...new Set([...prev.symbols, ...filteredSymbolsList])]
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'lookback'
        ? parseFloat(value) || 0 
        : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setSuccess('');
    if (formData.symbols.length === 0) {
      setError(t('select_symbol'));
      return;
    }
    setLoading(true);
    let completedBatchCount = 0;
    const scanDetails = { scanned: 0, signals: [], findings: [] };
    let existingPredictions = 0;
    const summarizeErrors = errors => {
      const messages = errors.slice(0, 3).map(failure =>
        `${failure.symbol ? `${failure.symbol}: ` : ''}${failure.error}`
      );
      if (errors.length > 3) {
        messages.push(t('scan_more_errors', {
          count: errors.length - 3,
          defaultValue: `${errors.length - 3} more error(s). See the scan notification for details.`,
        }));
      }
      return messages.join('\n');
    };

    try {
      const token = localStorage.getItem('token');

      if (formData.symbols.length === 1) {
        // Single symbol – use the original endpoint directly.
        setScanProgress({ done: 0, total: 1 });
        const response = await axios.post(
          `${API}/scanner/run`,
          { ...formData, symbol: formData.symbols[0] },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        setScanProgress({ done: 1, total: 1 });
        const result = response.data;
        existingPredictions = result.existing_predictions || 0;
        const totalSignals = result.signals?.length ?? 0;
        scanDetails.scanned = 1;
        scanDetails.findings = [result];
        scanDetails.signals = toScanSignalDetails(formData.symbols[0], result.signals);
        if (totalSignals > 0) {
          setSuccess(t('scan_summary_with_signals', {
            total: 1,
            signals: totalSignals,
            summary: `${formData.symbols[0]}(${totalSignals})`,
          }) + (result.existing_predictions > 0 ? ` ${t('scan_predictions_exist', { count: result.existing_predictions, defaultValue: `${result.existing_predictions} prediction(s) already exist; no duplicate inserted.` })}` : ''));
        } else {
          setSuccess(t('scan_summary_no_signals', { total: 1 }));
        }
      } else {
        // Save each symbol independently so confirmed signals can appear immediately.
        const total = formData.symbols.length;
        setScanProgress({ done: 0, total });

        let scanned = 0;
        let signalsFound = 0;
        const batchResults = [];
        // Keep each request bounded while scanning several batches at once.
        await runBatchesWithConcurrency(
          chunkSymbols(formData.symbols, SCAN_BATCH_SIZE),
          MAX_PARALLEL_SCAN_BATCHES,
          async (symbols) => {
            const response = await axios.post(
            `${API}/scanner/run-batch`,
            {
              ...formData,
              symbols,
              concurrency: SCAN_BATCH_SIZE,
            },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
          );
            return response.data;
          },
          (batch, symbols) => {
            batchResults.push(...(batch.results || []));
            const failures = [...(batch.failures || [])];
            const missingFailures = Math.max(0, (batch.errors || 0) - failures.length);
            if (missingFailures > 0) {
              failures.push({ error: t('scan_symbols_failed', {
                count: missingFailures,
                defaultValue: `${missingFailures} symbol(s) could not be scanned.`,
              }) });
            }
            if (failures.length) {
              scanDetails.errors = [...(scanDetails.errors || []), ...failures];
            }
            scanDetails.findings = [...batchResults];
            scanDetails.scanned += batch.scanned || 0;
            scanDetails.signals = batchResults.flatMap(result => toScanSignalDetails(result.symbol, result.signals));
            scanned += batch.scanned || 0;
            signalsFound += batch.signals_found || 0;
            completedBatchCount += 1;
            setScanProgress(p => ({ done: Math.min(p.done + symbols.length, total), total }));
            if (batch.signals_found > 0 && onScanComplete) {
              onScanComplete();
            }
          }
        );

        scanDetails.scanned = scanned;
        scanDetails.findings = batchResults;
        existingPredictions = batchResults.reduce((sum, result) => sum + (result.existing_predictions || 0), 0);
        scanDetails.signals = batchResults.flatMap(r => toScanSignalDetails(r.symbol, r.signals));

        if (signalsFound > 0) {
          const symbolsSummary = batchResults
            .filter(r => r.signals?.length > 0)
            .map(r => `${r.symbol}(${r.signals.length})`)
            .join(', ');
          setSuccess(t('scan_summary_with_signals', {
            total: scanned,
            signals: signalsFound,
            summary: symbolsSummary,
          }) + (existingPredictions > 0 ? ` ${t('scan_predictions_exist', { count: existingPredictions, defaultValue: `${existingPredictions} prediction(s) already exist; no duplicate inserted.` })}` : ''));
        } else if (scanned > 0) {
          setSuccess(t('scan_summary_no_signals', { total: scanned }));
        }
        if (scanDetails.errors?.length) {
          setError(summarizeErrors(scanDetails.errors));
        }
      }

      // Notify parent to refresh results
      if (onScanComplete) {
        onScanComplete();
      }
      if (onScanNotification) {
        if (existingPredictions > 0) scanDetails.existing_predictions = existingPredictions;
        onScanNotification(scanDetails);
      }
    } catch (err) {
      if (completedBatchCount > 0 && onScanComplete) {
        onScanComplete();
      }

      if (isRequestAborted(err)) {
        return;
      }

      const detail = Array.isArray(err.response?.data?.detail)
        ? err.response.data.detail.map(e => e.msg || String(e)).join('; ')
        : err.response?.data?.detail;
      const message = err.response?.status === 504
        ? t('scan_gateway_timeout')
        : detail || err.message || t('scan_failed');
      scanDetails.errors = [...(scanDetails.errors || []), {
        ...(formData.symbols.length === 1 ? { symbol: formData.symbols[0] } : {}),
        error: message,
      }];
      if (onScanNotification) {
        onScanNotification(scanDetails);
      }
      setError(summarizeErrors(scanDetails.errors));
    } finally {
      setLoading(false);
      setScanProgress({ done: 0, total: 0 });
    }
  };

  const notFoundSymbols = (() => {
    const searchTerms = searchSymbol
      .split(',')
      .map(term => term.trim().toLowerCase())
      .filter(Boolean);

    if (searchTerms.length <= 1) return [];

    return [...new Set(searchTerms.filter(term =>
      !symbolCatalog.some(item =>
        item.symbol.toLowerCase().includes(term) || item.name.toLowerCase().includes(term)
      )
    ).map(term => term.toUpperCase()))];
  })();

  return (
    <div className={`${isOptionsOpen
      ? 'relative'
      : 'sticky top-[var(--app-header-height)] z-40 lg:static lg:z-auto'
    } w-full flex-shrink-0 lg:h-full lg:min-h-0 lg:w-80 xl:w-96`}>
      <div className={`relative mx-auto flex h-auto w-full max-w-none flex-col bg-white ${
        isOptionsOpen ? 'shadow-sm' : 'shadow-none'
      } lg:sticky lg:top-0 lg:mx-0 lg:h-full lg:min-h-0 lg:shadow-xl`}>
        <div className="relative flex h-auto min-h-0 flex-col lg:h-full">
          {/* Header */}
          <div className="relative z-10 shrink-0 bg-white px-6 pt-3 pb-2 lg:sticky lg:top-0 lg:z-30">
            <div className="flex min-h-9 items-center justify-between gap-4 border-b border-blue-100 pb-2">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                {t('scanner_options')}
                {loading && !isOptionsOpen && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 lg:hidden" aria-live="polite">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 0 18-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A 7.962 7.962 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {scanProgress.total > 0 && `${scanProgress.done}/${scanProgress.total}`}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={toggleOptions}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-gray-600 hover:bg-gray-100 lg:hidden"
                aria-label={isOptionsOpen ? 'Collapse Scanner' : 'Expand Scanner'}
                aria-expanded={isOptionsOpen}
                aria-controls="scanner-options-content"
              >
                <svg
                  className={`h-5 w-5 transition-transform ${isOptionsOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path d="m5 7.5 5 5 5-5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Form Content */}
          <div
            id="scanner-options-content"
            className={`${isOptionsOpen ? 'flex' : 'hidden'} flex-1 flex-col px-6 pb-6 min-h-0 lg:flex lg:overflow-y-auto`}
          >
            <div className="shrink-0 space-y-4 pt-2">
            <div className="space-y-4">
            {/* Symbol Selection */}
            <div>
              <div role="group" aria-label={t('scanner_symbol_universe', { defaultValue: 'Symbol universe' })} className="mb-3 flex rounded-lg border border-gray-300 p-1">
                {[['sp500', 'S&P 500'], ['etf', 'ETF']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={symbolUniverse === value}
                    onClick={() => setSymbolUniverse(value)}
                    className={`min-h-8 flex-1 rounded-md px-2 py-1 text-xs font-medium transition ${symbolUniverse === value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-blue-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
                  {/* Search Box */}
                  <input
                    type="text"
                    placeholder={t('search_by_symbol')}
                    value={searchSymbol}
                    onChange={(e) => setSearchSymbol(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg mb-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                  />

              {notFoundSymbols.length > 0 && (
                <div className="mb-2 p-2 border border-red-200 bg-red-50 rounded-lg">
                  <div className="text-xs font-semibold text-red-700 mb-1">Not found:</div>
                  <div className="flex flex-wrap gap-1">
                    {notFoundSymbols.map((symbol) => (
                      <span
                        key={`missing-${symbol}`}
                        className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-200"
                      >
                        {symbol} (not found)
                      </span>
                    ))}
                </div>
              </div>
              )}
              
              {/* Symbol List with Checkboxes */}
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-300 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {filteredSymbols().length > 0 ? (
                  <>
                    <div className="sticky top-0 bg-gray-50 px-3 text-xs border-b">
                      <label className="flex min-h-9 w-full cursor-pointer touch-manipulation items-center gap-2">
                        <input
                          type="checkbox"
                          checked={areAllFilteredSelected()}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = areSomeFilteredSelected();
                            }
                          }}
                          onChange={handleMasterCheckbox}
                          className="h-4 w-4 shrink-0 cursor-pointer"
                          title={areAllFilteredSelected() ? t('deselect_all') : t('select_all')}
                        />
                        <span className="font-medium text-gray-700">
                          {areAllFilteredSelected() ? t('deselect_all') : t('select_all')}
                        </span>
                        <span className="ml-auto text-gray-600">
                          {t('symbols_found_count', { count: filteredSymbols().length })}
                        </span>
                      </label>
                    </div>
                    {filteredSymbols().map((item) => (
                      <div
                        key={item.symbol}
                        onClick={() => handleSymbolToggle(item.symbol)}
                        className={`
                          px-3 py-1 cursor-pointer hover:bg-blue-50 transition flex items-center gap-2
                          ${formData.symbols.includes(item.symbol) ? 'bg-blue-50' : ''}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={formData.symbols.includes(item.symbol)}
                          onChange={() => {}} // Handled by div onClick
                          className="w-3.5 h-3.5"
                        />
                        <div className="flex items-center justify-between flex-1">
                          <span className={`text-sm font-medium text-gray-800 ${formData.symbols.includes(item.symbol) ? 'text-blue-600' : ''}`}>
                            {item.symbol}
                          </span>
                          <span className="ml-2 min-w-0 flex-1 whitespace-normal break-words text-right text-xs text-gray-500">{item.name}</span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {t('no_symbols_found')}
                  </div>
                )}
              </div>
              
              {/* Selected Symbols Display */}
              <div className="mt-2">
                <div className="text-sm text-gray-600 mb-1">
                  {t('selected_count_label', { count: formData.symbols.length })}
                </div>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {formData.symbols.map(symbol => (
                    <span 
                      key={symbol}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                    >
                      {symbol}
                      <button
                        type="button"
                        onClick={() => handleSymbolToggle(symbol)}
                        className="hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              </div>

            {/* Save and Load Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowSaveModal(true)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition"
                disabled={formData.symbols.length === 0}
              >
                <BookmarkPlus className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
                {t('save_current')}
              </button>
              <button
                type="button"
                onClick={() => setShowSavedLists(!showSavedLists)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition"
              >
                <Download className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
                {`${t('saved_lists')} (${savedLists.length})`}
              </button>
            </div>

            {/* Saved Lists Dropdown */}
            {showSavedLists && savedLists.length > 0 && (
              <div className="border border-purple-300 rounded-lg p-3 bg-purple-50 max-h-48 overflow-y-auto">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold text-purple-800">{t('saved_lists_heading')}</div>
                  <button
                    type="button"
                    onClick={() => setShowSavedLists(false)}
                    className="rounded px-2 text-sm font-semibold text-purple-700 hover:bg-purple-100"
                    aria-label="Close saved symbols"
                    title="Close saved symbols"
                  >
                    ×
                  </button>
                </div>
                {savedLists.map(list => (
                  <div key={list.list_id} className="flex items-center justify-between py-2 border-b border-purple-200 last:border-0">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">{list.name}</div>
                      <div className="text-xs text-gray-600">{t('symbol_count_label', { count: list.symbol_count })}</div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleLoadList(list)}
                        className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                        title={t('load_symbols_tooltip')}
                      >
                        {t('load')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRunSavedList(list)}
                        className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                        title={t('run_saved_list_tooltip')}
                      >
                        {t('run_now')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteList(list.list_id, list.name)}
                        className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                        title={t('delete_list_tooltip')}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
              {/* Period */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('period')}
                </label>
                <select
                  name="period"
                  value={formData.period}
                  onChange={handleInputChange}
                    className="w-full min-w-0 px-1 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {PERIODS.map(p => (
                    <option key={p.value} value={p.value} disabled={formData.interval === '1m' && isPeriodOverSevenDays(p.value)}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* Interval */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('interval')}
                </label>
                <select
                  name="interval"
                  value={formData.interval}
                  onChange={handleInputChange}
                    className="w-full min-w-0 px-1 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {INTERVALS.map(i => (
                    <option key={i.value} value={i.value} disabled={i.value === '1m' && isPeriodOverSevenDays(formData.period)}>{i.label}</option>
                  ))}
                </select>
              </div>
              <label className="block whitespace-nowrap text-xs font-medium text-gray-700">
                <span className="mb-2 block leading-5">{t('include_extended_hours', { defaultValue: 'Include Extended Hours' })}</span>
                <span className="flex h-[30px] items-center">
                  <input
                    type="checkbox"
                    role="switch"
                    aria-checked={formData.include_extended_hours}
                    name="include_extended_hours"
                    checked={formData.include_extended_hours}
                    onChange={handleInputChange}
                    className="peer sr-only"
                  />
                  <span aria-hidden="true" className="relative h-5 w-9 shrink-0 cursor-pointer rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-blue-600 peer-checked:after:translate-x-4 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2" />
                </span>
              </label>
            </div>

            {success && (
              <div className="bg-green-100 text-green-800 p-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            {error && (
              <div className="bg-red-100 text-red-800 p-3 rounded-lg text-sm whitespace-pre-line">
                {error}
              </div>
            )}

            <AutomaticScanner formData={formData} onScanComplete={onScanComplete} onScanNotification={onScanNotification} onRunScanner={handleSubmit} scanning={loading} scanProgress={scanProgress} />

            </div>

          </div>
        </div>
      </div>

      {/* Removed sticky bottom button; action moved beneath header */}
      </div>

      {/* Save List Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{`💾 ${t('save_symbol_list_title')}`}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('save_symbol_list_description', { count: formData.symbols.length })}
            </p>
            <input
              type="text"
              placeholder={t('enter_list_name')}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4 focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleSaveList()}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSaveList}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                disabled={!newListName.trim()}
              >
                {t('save')}
              </button>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setNewListName('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScannerMenu;
